import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Radio,
  Checkbox,
  TextField,
  InputAdornment,
  LinearProgress,
  Chip,
  Alert,
  Divider,
} from "@mui/material";
import { Close, Search, DirectionsBoat } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import dayjs from "dayjs";

import api from "../../api/axiosInstance";

// Sailing assignment is per ITEM, not per whole shipment — this dialog is
// opened from a single shipment's detail page, so the flow is: 1) pick a
// sailing, 2) pick which of THIS shipment's eligible (received, unassigned,
// not in a consolidation) items go on it. For the "pick a sailing, then
// pick items across many shipments" flow, see AssignShipmentsDialog (used
// from the Sailing pages).
const AssignSailingDialog = ({ open, shipment, onClose, onAssigned }) => {
  const { enqueueSnackbar } = useSnackbar();

  const [sailings, setSailings] = useState([]);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);

  const [items, setItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [selectedItemIds, setSelectedItemIds] = useState(new Set());

  useEffect(() => {
    if (!open) return;

    setSelected(null);
    setSearch("");
    setLoading(true);
    setSelectedItemIds(new Set());

    api
      .get("/sailing")
      .then(({ data }) => setSailings(data.data?.schedules || []))
      .catch(() =>
        enqueueSnackbar("Failed to load sailings", { variant: "error" }),
      )
      .finally(() => setLoading(false));

    setItemsLoading(true);
    api
      .get("/sailing/assignable")
      .then(({ data }) => {
        const all = data.data?.items || [];
        const forShipment = all.filter((it) => it.shipmentId === shipment?._id);
        setItems(forShipment);
        setSelectedItemIds(new Set(forShipment.map((it) => it._id)));
      })
      .catch(() => setItems([]))
      .finally(() => setItemsLoading(false));
  }, [open, shipment]);

  const activeSailings = sailings.filter((s) =>
    ["scheduled", "loading", "sailing"].includes(s.status),
  );

  const filtered = activeSailings.filter((s) => {
    const q = search.toLowerCase();
    return (
      s.sailingNumber?.toLowerCase().includes(q) ||
      s.vesselName?.toLowerCase().includes(q) ||
      s.originPort?.toLowerCase().includes(q) ||
      s.destinationPort?.toLowerCase().includes(q)
    );
  });

  const toggleItem = (id) => {
    setSelectedItemIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleAssign = async () => {
    if (!selected || selectedItemIds.size === 0) return;

    setAssigning(true);
    try {
      const itemRefs = [...selectedItemIds].map((itemId) => ({
        shipmentId: shipment._id,
        itemId,
      }));
      const { data } = await api.post(`/sailing/${selected}/assign`, {
        items: itemRefs,
      });

      enqueueSnackbar(`${itemRefs.length} item(s) assigned successfully`, {
        variant: "success",
      });

      onAssigned(data.data?.schedule);
      onClose();
    } catch (err) {
      enqueueSnackbar(
        err.response?.data?.message || "Failed to assign items",
        { variant: "error" },
      );
    } finally {
      setAssigning(false);
    }
  };

  const selectedSailing = sailings.find((s) => s._id === selected);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between" }}>
        <Box>
          <Typography fontWeight={700}>Assign to Sailing</Typography>
          <Typography variant="caption" color="text.secondary">
            Select a sailing for this shipment
          </Typography>
        </Box>

        <IconButton onClick={onClose}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* Search */}
        <Box sx={{ p: 2 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search sailing..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Box>

        {loading && <LinearProgress />}
        {!loading && activeSailings.length === 0 && (
          <Alert severity="warning" sx={{ m: 2 }}>
            No active sailings available. Only scheduled, loading, or sailing
            voyages can receive shipments.
          </Alert>
        )}

        {!loading && activeSailings.length > 0 && (
          <Box sx={{ maxHeight: 420, overflowY: "auto" }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell width={50}></TableCell>
                  <TableCell>Sailing #</TableCell>
                  <TableCell>Vessel</TableCell>
                  <TableCell>Voyage #</TableCell>
                  <TableCell>BOL #</TableCell>
                  <TableCell>Route</TableCell>
                  <TableCell>Departure</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {filtered.map((s) => {
                  const isSelected = selected === s._id;

                  return (
                    <TableRow
                      key={s._id}
                      hover
                      onClick={() => setSelected(s._id)}
                      sx={{
                        cursor: "pointer",
                        bgcolor: isSelected
                          ? "rgba(0,0,0,0.04)"
                          : "transparent",
                      }}
                    >
                      <TableCell>
                        <Radio checked={isSelected} />
                      </TableCell>

                      <TableCell sx={{ fontFamily: "monospace" }}>
                        {s.sailingNumber}
                      </TableCell>

                      <TableCell>{s.vesselName}</TableCell>

                      <TableCell>{s.voyageNumber || "—"}</TableCell>

                      <TableCell sx={{ fontFamily: "monospace" }}>
                        {s.masterBillNumber || "—"}
                      </TableCell>

                      <TableCell>
                        {s.originPort} → {s.destinationPort}
                      </TableCell>

                      <TableCell>
                        {dayjs(s.departureDate).format("MMM D, YYYY")}
                      </TableCell>

                      <TableCell>
                        <Chip
                          size="small"
                          label={s.status}
                          color={s.status === "active" ? "success" : "default"}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        )}

        {selectedSailing && (
          <Box sx={{ p: 2, bgcolor: "action.hover", borderTop: "1px solid", borderColor: "divider" }}>
            <Typography variant="body2" fontWeight={600}>
              Selected:
            </Typography>
            <Typography variant="caption">
              {selectedSailing.sailingNumber} · {selectedSailing.vesselName}
              {selectedSailing.voyageNumber ? ` / ${selectedSailing.voyageNumber}` : ""}
              {selectedSailing.masterBillNumber ? ` · BOL ${selectedSailing.masterBillNumber}` : ""}
            </Typography>
          </Box>
        )}

        <Divider />

        <Box sx={{ p: 2 }}>
          <Typography variant="overline" color="text.secondary" display="block" mb={1}>
            Items to Assign
          </Typography>

          {itemsLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={22} />
            </Box>
          ) : items.length === 0 ? (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              This shipment has no items eligible for sailing assignment right now — they must be{" "}
              <strong>Item Received</strong>, not already on a sailing, and not part of a
              consolidation.
            </Alert>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              {items.map((it) => (
                <Box key={it._id} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Checkbox
                    size="small"
                    checked={selectedItemIds.has(it._id)}
                    onChange={() => toggleItem(it._id)}
                  />
                  <Typography variant="body2" sx={{ flex: 1 }} noWrap>
                    {it.description}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
                    {it.trackingNumber || "—"}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>Cancel</Button>

        <Button
          variant="contained"
          disabled={!selected || selectedItemIds.size === 0 || assigning}
          onClick={handleAssign}
          startIcon={assigning ? null : <DirectionsBoat />}
        >
          {assigning ? (
            <CircularProgress size={18} color="inherit" />
          ) : (
            `Assign ${selectedItemIds.size > 0 ? selectedItemIds.size : ""} Item${selectedItemIds.size !== 1 ? "s" : ""}`
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignSailingDialog;
